import React, { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Database,
  Table as TableIcon,
  Terminal,
} from "lucide-react";

import api from "../../api";
import { ScrollArea } from "../ui/scroll-area";

const formatTableName = (tableName) =>
  tableName
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const getTableDisplayName = (tableName) => formatTableName(tableName);

export default function AdminLayout() {
  const { table } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [availableTables, setAvailableTables] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    database: true,
    dbAdmin: true,
    tasks: true,
  });
  const [tablesExpanded, setTablesExpanded] = useState(false);

  const fetchTables = useCallback(async () => {
    try {
      const response = await api.get("/admin/tables");
      const sorted = response.data.sort((a, b) =>
        getTableDisplayName(a).localeCompare(getTableDisplayName(b))
      );
      setAvailableTables(sorted);
    } catch (e) {
      console.error("Error fetching tables:", e);
      setAvailableTables([]);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const toggleSection = (section) =>
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

  const isOverview = location.pathname === "/admin" || location.pathname === "/admin/";
  const isDbAdmin =
    location.pathname === "/admin/db" || location.pathname.startsWith("/admin/db/");
  const isTasks = table === "tasks";
  const isTableView = !!table && !isTasks;

  useEffect(() => {
    if (isTableView) {
      setTablesExpanded(true);
    }
  }, [isTableView]);

  return (
    <div className="flex h-screen bg-background">
      <aside className="w-64 min-w-64 border-r bg-card">
        <div className="p-4 border-b">
          <Link
            to="/admin"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <Database className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Admin Panel</h1>
          </Link>
        </div>
        <ScrollArea className="h-[calc(100vh-73px)] scrollbar-skinny">
          <div className="p-4 space-y-1">
            {/* Database Section */}
            <div className="mb-4">
              <SectionToggle
                label="Database"
                Icon={Database}
                expanded={expandedSections.database}
                onClick={() => toggleSection("database")}
              />
              {expandedSections.database && (
                <div className="mt-1 space-y-1 ml-4">
                  <NavLink to="/admin" Icon={Database} active={isOverview}>
                    Overview
                  </NavLink>
                  <SectionToggle
                    label="Tables"
                    Icon={TableIcon}
                    expanded={tablesExpanded}
                    onClick={() => setTablesExpanded((v) => !v)}
                  />
                  {tablesExpanded && (
                    <div className="mt-1 space-y-1 ml-4">
                      {availableTables.map((tableName) => (
                        <NavLink
                          key={tableName}
                          to={`/admin/${tableName}`}
                          Icon={TableIcon}
                          active={isTableView && table === tableName}
                        >
                          {getTableDisplayName(tableName)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Db Admin Section */}
            <div className="mb-4">
              <SectionToggle
                label="Db Admin"
                Icon={Database}
                expanded={expandedSections.dbAdmin}
                onClick={() => toggleSection("dbAdmin")}
              />
              {expandedSections.dbAdmin && (
                <div className="mt-1 space-y-1 ml-4">
                  <NavLink
                    to="/admin/db?view=browse"
                    Icon={Database}
                    active={isDbAdmin && searchParams.get("view") !== "query"}
                  >
                    Browse Tables
                  </NavLink>
                  <NavLink
                    to="/admin/db?view=query"
                    Icon={Terminal}
                    active={isDbAdmin && searchParams.get("view") === "query"}
                  >
                    SQL Query
                  </NavLink>
                </div>
              )}
            </div>

            {/* Tasks Section */}
            <div>
              <SectionToggle
                label="Tasks"
                Icon={Calendar}
                expanded={expandedSections.tasks}
                onClick={() => toggleSection("tasks")}
              />
              {expandedSections.tasks && (
                <div className="mt-1 space-y-1 ml-4">
                  <NavLink to="/admin/tasks" Icon={Calendar} active={isTasks}>
                    Background Tasks
                  </NavLink>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </aside>

      <Outlet context={{ availableTables, refreshTables: fetchTables }} />
    </div>
  );
}

function SectionToggle({ label, Icon, expanded, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-semibold hover:bg-accent hover:text-accent-foreground transition-colors"
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span>{label}</span>
      </div>
      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
  );
}

function NavLink({ to, Icon, active, children }) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
        active
          ? "bg-primary text-primary-foreground"
          : "hover:bg-accent hover:text-accent-foreground"
      }`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="whitespace-nowrap">{children}</span>
    </Link>
  );
}
